import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { OrderingDirections } from 'generated/graphql-types';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import { devicesRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddDeviceComponent } from '../../add-devices/containers/add-device.component';
import { convertDeviceRegisterListItem } from '../helpers/devices-helper';
import { DevicesFilterOption } from '../models/device-filter-option';
import { DeviceOrderingOptions, DeviceRegisterOptions } from '../models/device-register-options';
import { DeviceRegisterResults } from '../models/device-register-results.model';
import { GetDevices } from '../queries/generated/GetDevices';
import { DeviceAccountRegisterSessionStorageService } from '../services/account-device-register-session.storage.service';
import { DeviceRegisterApiService } from '../services/device-register-api.service';
import { DeviceSystemRegisterSessionStorageService } from '../services/system-device-register-session.storage.service';
import {
   changeSipUserPasswordFailure,
   changeSipUserPasswords,
   changeSipUserPasswordSuccess,
   createDevice,
   deviceRegisterAdapterActions,
   loadAccountDevicesSuccess,
   loadSystemDevicesSuccess,
   rebootPhone,
   rebootPhoneFailure,
   rebootPhoneSuccess,
   resetPhoneZtp,
   resetPhoneZtpFailure,
   resetPhoneZtpSuccess,
   setOrderingOptionsFromSessionStorage,
} from './device-register.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './device-register.reducer';
import * as DeviceRegisterSelectors from './device-register.selectors';
import { selectFilters } from './device-register.selectors';

@Injectable()
export class DeviceRegisterEffects {
   addDevice$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createDevice),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddDeviceComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadDeviceList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(deviceRegisterAdapterActions.pageEnter, changeSipUserPasswordSuccess),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectCurrentRoute)),
            this.store.pipe(select(DeviceRegisterSelectors.selectIsOnSystemPage))
         ),
         switchMap(([, filters, quickFilters, route, wasPreviouslyOnSystemPage]) => {
            const path = route.url[0].path;
            var orderingOptions;
            var sessionOrderingOptions;
            if (path === devicesRoute) {
               orderingOptions = [{ direction: OrderingDirections.Asc, field: 'account.name' }];
               sessionOrderingOptions = this.deviceSystemRegisterSessionStorageService.getSystemDeviceOrderingOptions();
               if (!wasPreviouslyOnSystemPage) {
                  this.store.dispatch(deviceRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(loadSystemDevicesSuccess());
            } else {
               orderingOptions = [{ direction: OrderingDirections.Asc, field: 'deviceType.name' }];
               sessionOrderingOptions =
                  this.deviceAccountRegisterSessionStorageService.getAccountDeviceOrderingOptions();
               if (wasPreviouslyOnSystemPage) {
                  this.store.dispatch(deviceRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(loadAccountDevicesSuccess());
            }
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions: orderingOptions }));
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadDevicePage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(deviceRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(DeviceRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([action, filters, endCursor, quickFilter, orderingOptions, route]) => {
            const path = route.url[0].path;
            var sessionOrderingOptions;
            if (path === devicesRoute) {
               sessionOrderingOptions = this.deviceSystemRegisterSessionStorageService.getSystemDeviceOrderingOptions();
            } else {
               sessionOrderingOptions =
                  this.deviceAccountRegisterSessionStorageService.getAccountDeviceOrderingOptions();
            }

            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewPage(endCursor, filters, quickFilter, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(deviceRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(DeviceRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions, route]) => {
            const path = route.url[0].path;
            if (path === devicesRoute) {
               this.deviceSystemRegisterSessionStorageService.setSystemDeviceOrderingOptions(orderingOptions);
            } else {
               this.deviceAccountRegisterSessionStorageService.setAccountDeviceOrderingOptions(orderingOptions);
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(deviceRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([action, quickfilters, orderingOptions, filters, route]) => {
            const path = route.url[0].path;
            if (path === devicesRoute) {
               this.deviceSystemRegisterSessionStorageService.setSystemDeviceOrderingOptions(orderingOptions);
            } else {
               this.deviceAccountRegisterSessionStorageService.setAccountDeviceOrderingOptions(orderingOptions);
            }
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   changeSipUserPassword$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(changeSipUserPasswords),
         switchMap((action) => {
            return this.deviceRegisterApiService.changeSipUserPasswords(action.sipUserIds).pipe(
               map(() => {
                  return changeSipUserPasswordSuccess();
               }),
               catchError((error) => {
                  return of(changeSipUserPasswordFailure(error));
               })
            );
         })
      );
   });

   rebootPhone$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(rebootPhone),
         switchMap((action) => {
            const message = 'Are you sure you want to reboot this phone?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Reboot This Phone?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.deviceRegisterApiService.rebootPhone(action.deviceId).pipe(
                        map(() => {
                           return rebootPhoneSuccess();
                        }),
                        catchError((error) => {
                           return of(rebootPhoneFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   rebootPhoneSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(rebootPhoneSuccess),
            tap((action) => {
               this.snackBar.open('Reboot Initiated for the selected Phone');
            })
         );
      },
      { dispatch: false }
   );

   resetPhoneZtp$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(resetPhoneZtp),
         switchMap((action) => {
            const message = 'Are you sure you want to reset this phone via Ztp?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Reset This Phone via Ztp?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.deviceRegisterApiService.resetPhone(action.deviceId).pipe(
                        map(() => {
                           return resetPhoneZtpSuccess();
                        }),
                        catchError((error) => {
                           return of(resetPhoneZtpFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   resetPhoneZtpSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(resetPhoneZtpSuccess),
            tap((action) => {
               this.snackBar.open('Reset Initiated for the selected Phone via Ztp');
            })
         );
      },
      { dispatch: false }
   );

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private deviceRegisterApiService: DeviceRegisterApiService,
      private dialogService: LcsDialogService,
      private snackBar: MatSnackBar,
      private confirmationService: ConfirmationDialogService,
      private deviceSystemRegisterSessionStorageService: DeviceSystemRegisterSessionStorageService,
      private deviceAccountRegisterSessionStorageService: DeviceAccountRegisterSessionStorageService
   ) {}

   private convertGetDevicesResultToLoadSuccessProps(getDevices: GetDevices): DeviceRegisterResults {
      return {
         endCursor: getDevices.devicesConnection.pageInfo.endCursor,
         hasNextPage: getDevices.devicesConnection.pageInfo.hasNextPage,
         items: getDevices.devicesConnection.items.map(convertDeviceRegisterListItem.bind(this)),
         totalCount: getDevices.devicesConnection.totalCount,
      };
   }

   private loadNewData(
      filters: DevicesFilterOption[],
      quickFilters: DeviceRegisterOptions | null = null,
      orderingOptions: DeviceOrderingOptions[] | null = null
   ) {
      return this.deviceRegisterApiService.getDevices(null, filters, quickFilters, orderingOptions, null).pipe(
         map((result) => {
            return deviceRegisterAdapterActions.loadSuccess(
               this.convertGetDevicesResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(deviceRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: DevicesFilterOption[],
      quickFilters: DeviceRegisterOptions | null = null,
      orderingOptions: DeviceOrderingOptions[] | null = null
   ) {
      return this.deviceRegisterApiService.getDevices(endCursor, filters, quickFilters, orderingOptions, null).pipe(
         map((result) => {
            return deviceRegisterAdapterActions.loadPageSuccess(
               this.convertGetDevicesResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(deviceRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }
}
